import Logo from "assets/svg/logo.svg";
import LogoWhite from "assets/svg/logo.svg";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useState, useRef, useEffect } from "react";
import { useModal, ModalType } from "context/modal-context";
import StacstartImg from "assets/png/stacstart.png";
import MelonImg from "assets/png/Melon.png";
import TracnineImg from "assets/png/Tracnine.png";
import CodeIcon from "assets/svg/code.svg";
import CloudIcon from "assets/svg/cloud.svg";
import CircleIcon from "assets/svg/circle.svg";

interface Values {
  colors?: string;
  weight?: string;
  active?: number;
}

const IconBox = ({ icon }: { icon: string }) => (
  <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg shrink-0">
    <img src={icon} alt="" className="w-5 h-5" />
  </div>
);

const services: { label: string; icon: string; modal: ModalType }[] = [
  { label: "Tech Talent Recruitment and Outsourcing", icon: CodeIcon, modal: "hire-talent" },
  { label: "Web and Software Development / Project Delivery", icon: CloudIcon, modal: "start-project" },
  { label: "Managed Technology Services", icon: CircleIcon, modal: "managed-service" },
  { label: "Corporate and Bespoke Training", icon: CircleIcon, modal: "corporate-training" },
];

const academyLinks = [
  { label: "Stacstart", to: "https://stacstart.com/" },
  { label: "Stactstart US", to: "https://conclaseacademy.us" },
];

const products = [
  {
    name: "Stacstart",
    subtitle: "AI- Powered Talent Engine",
    to: "https://stacstart.com/",
    btnLabel: "Explore Stacstart",
    comingSoon: false,
    image: StacstartImg,
  },
  {
    name: "Melon",
    subtitle: "Digital Signage solution",
    to: "https://melonsign.com/",
    btnLabel: "Explore Melon",
    comingSoon: false,
    image: MelonImg,
  },
  {
    name: "Tracnine",
    subtitle: "Delivery and order management",
    to: "https://melonsign.com/",
    btnLabel: "Explore Tracnine",
    comingSoon: false,
    image: TracnineImg,
  },
];

const Navbar = ({ colors = "dark", weight = "font-semibold", active }: Values) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileOne, setMobileOne] = useState(false);
  const [mobileTwo, setMobileTwo] = useState(false);
  const [mobileThree, setMobileThree] = useState(false);
  const [megaMenu, setMegaMenu] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);

  const hoverTimer = useRef<any>(null);
  const navigate = useNavigate();
  const { openModal } = useModal();

  const isLight = colors?.includes("bg-white");
  const isTransparent = colors === "transparent";

  useEffect(() => {
    if (!isTransparent) return;
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [isTransparent]);

  const navBg = isTransparent ? (scrolled ? "bg-[#080B1F]" : "bg-transparent") : isLight ? "bg-white" : "bg-[#080B1F]";
  const linkClass = isLight
    ? `${weight} text-grey-500 hover:text-blue-100 transition-colors`
    : `${weight} text-white hover:text-yellow-100 transition-colors`;
  const mobileLink = isLight ? "text-grey-500" : "text-white";
  const mobileSubLink = isLight ? "text-grey-500/80" : "text-white/70";

  const openMenu = (key: string) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setMegaMenu(key);
  };

  const closeMenu = () => {
    hoverTimer.current = setTimeout(() => setMegaMenu(null), 120);
  };

  const cancelClose = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  };

  const handleMobileSection = (row: number) => {
    setMobileOne(row === 1 ? !mobileOne : false);
    setMobileTwo(row === 2 ? !mobileTwo : false);
    setMobileThree(row === 3 ? !mobileThree : false);
  };

  return (
    <div className="relative w-full">
      {/* Spacer */}
      <div className="h-20" />

      {createPortal(
        <>
      {/* ── Navbar bar ───────────────────────────────────────────────────── */}
      <div className={`fixed top-0 left-0 z-50 w-full h-20 px-6 xl:px-20 flex items-center justify-between transition-colors duration-300 ${navBg}`}>

        <Link to="/" onClick={() => setMegaMenu(null)}>
          <img src={isLight ? Logo : LogoWhite} alt="logo" className="object-contain w-[120px] h-[64px] lg:w-[150px] lg:h-[80px]" />
        </Link>

        {/* Desktop links */}
        <div className="items-center hidden lg:flex gap-7">
          <Link to="/about" className={`${linkClass} ${active === 3 ? "text-yellow-100" : ""}`}>
            About us
          </Link>

          <Link to="/corporate-training" className={`${linkClass} ${active === 9 ? "text-yellow-100" : ""}`}>
            Corporate Training
          </Link>

          {/* Our Services — hover trigger */}
          <button
            onMouseEnter={() => openMenu("services")}
            onMouseLeave={closeMenu}
            className={`flex items-center gap-1 ${linkClass} ${megaMenu === "services" ? "!text-yellow-100" : ""}`}
          >
            Our Services{" "}
            {megaMenu === "services" ? <FaChevronUp className="text-[10px] mt-0.5" /> : <FaChevronDown className="text-[10px] mt-0.5" />}
          </button>

          {/* Our Products — hover trigger */}
          <button
            onMouseEnter={() => openMenu("products")}
            onMouseLeave={closeMenu}
            className={`flex items-center gap-1 ${linkClass} ${megaMenu === "products" ? "!text-yellow-100" : ""}`}
          >
            Our Products{" "}
            {megaMenu === "products" ? <FaChevronUp className="text-[10px] mt-0.5" /> : <FaChevronDown className="text-[10px] mt-0.5" />}
          </button>

          {/* Academy — hover trigger */}
          <button
            onMouseEnter={() => openMenu("academy")}
            onMouseLeave={closeMenu}
            className={`flex items-center gap-1 ${linkClass} ${
              active === 4 || active === 8 || megaMenu === "academy" ? "!text-yellow-100" : ""
            }`}
          >
            Academy{" "}
            {megaMenu === "academy" ? <FaChevronUp className="text-[10px] mt-0.5" /> : <FaChevronDown className="text-[10px] mt-0.5" />}
          </button>
        </div>

        {/* Desktop right buttons */}
        <div className="items-center hidden gap-3 lg:flex">
          <button
            onClick={() => openModal("become-partner")}
            className={`px-5 py-2.5 font-semibold rounded-full border transition-all ${
              isLight
                ? "border-blue-100 text-blue-100 hover:bg-blue-100 hover:text-white"
                : "border-white text-white hover:bg-white hover:text-blue-100"
            }`}
          >
            Become a Partner
          </button>
          <button
            onClick={() => openModal("get-in-touch")}
            className={`px-5 py-2.5 font-semibold rounded-full transition-all ${
              isLight
                ? "bg-blue-100 text-white hover:bg-blue-200"
                : "bg-white text-blue-100 hover:bg-yellow-100"
            }`}
          >
            Contact Us
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="flex flex-col gap-1.5 cursor-pointer lg:hidden p-1"
          onClick={() => { setMobileOpen(!mobileOpen); setMegaMenu(null); }}
        >
          <span className={`w-6 h-0.5 block ${isLight ? "bg-gray-700" : "bg-white"}`} />
          <span className={`w-6 h-0.5 block ${isLight ? "bg-gray-700" : "bg-white"}`} />
          <span className={`w-6 h-0.5 block ${isLight ? "bg-gray-700" : "bg-white"}`} />
        </button>
      </div>

      {/* ── Dark overlay ──────────────────────────────────────────────────── */}
      {megaMenu && (
        <div
          className="fixed inset-0 z-40 top-20 bg-black/60"
          onMouseEnter={closeMenu}
        />
      )}

      {/* ── MEGA MENU: Our Services ───────────────────────────────────────── */}
      {megaMenu === "services" && (
        <div
          className="fixed left-0 z-50 flex justify-center w-full px-6 pt-4 pb-8 top-20 xl:px-20"
          onMouseEnter={cancelClose}
          onMouseLeave={closeMenu}
        >
          <div className="w-full max-w-[780px] flex flex-col gap-2">
            <p className="pl-1 text-sm font-semibold tracking-wide text-white">Service</p>
            <div className="flex flex-col p-2 bg-white divide-y divide-gray-100 rounded-2xl">
              {services.map((s) => (
                <button
                  key={s.label}
                  onClick={() => { setMegaMenu(null); openModal(s.modal); }}
                  className="flex items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-gray-50 rounded-xl"
                >
                  <IconBox icon={s.icon} />
                  <span className="font-medium text-dark-400">{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── MEGA MENU: Our Products ───────────────────────────────────────── */}
      {megaMenu === "products" && (
        <div
          className="fixed left-0 z-50 flex justify-center w-full px-6 pt-4 pb-8 top-20 xl:px-20"
          onMouseEnter={cancelClose}
          onMouseLeave={closeMenu}
        >
          <div className="w-full max-w-[900px] flex flex-col gap-2">
            <p className="pl-1 text-sm font-semibold tracking-wide text-white">Our Products</p>
            <div className="p-5 overflow-x-auto bg-white rounded-2xl">
              <div className="flex gap-5 min-w-[600px]">
                {products.map((p) => (
                  <div key={p.name} className="flex flex-col gap-4 flex-1 min-w-[200px]">
                  
                    <img src={p.image} alt={p.name} className="object-cover w-full h-full rounded" />
                    
                    <p className="text-lg font-black text-blue-100">{p.name}</p>
                    <p className="-mt-2 text-sm text-dark-100">{p.subtitle}</p>
                    <button
                      onClick={() => { if (!p.comingSoon) navigate(p.to); setMegaMenu(null); }}
                      disabled={p.comingSoon}
                      className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all w-fit ${
                        p.comingSoon
                          ? "bg-yellow-300 text-yellow-700 cursor-not-allowed opacity-70"
                          : "bg-yellow-100 text-blue-100 hover:bg-yellow-200"
                      }`}
                    >
                      {p.btnLabel}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MEGA MENU: Academy ────────────────────────────────────────────── */}
      {megaMenu === "academy" && (
        <div
          className="fixed left-0 z-50 flex justify-center w-full px-6 pt-4 pb-8 top-20 xl:px-20"
          onMouseEnter={cancelClose}
          onMouseLeave={closeMenu}
        >
          <div className="w-full max-w-[780px] flex flex-col gap-2">
            <p className="pl-1 text-sm font-semibold tracking-wide text-white">Academy</p>
            <div className="bg-white rounded-2xl p-2 flex flex-col divide-y divide-gray-100 w-full sm:w-[420px]">
              {academyLinks.map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  onClick={() => setMegaMenu(null)}
                  className="flex items-center gap-4 px-4 py-4 transition-colors hover:bg-gray-50 rounded-xl"
                >
                  <IconBox icon={CodeIcon} />
                  <span className="font-medium text-dark-400">{a.label}</span>
                </Link>
              ))}
             
             
            </div>
          </div>
        </div>
      )}

    
      {mobileOpen && (
        <div className={`fixed left-0 z-50 flex flex-col w-full gap-5 p-8 overflow-y-scroll top-20 lg:hidden h-dvh ${isLight ? "bg-white" : "bg-[#080B1F]"}`}>
          <Link to="/" className={`${mobileLink} font-semibold`} onClick={() => setMobileOpen(false)}>Home</Link>
          <Link to="/about" className={`${mobileLink} font-semibold`} onClick={() => setMobileOpen(false)}>About Us</Link>
          <Link to="/corporate-training" className={`${mobileLink} font-semibold`} onClick={() => setMobileOpen(false)}>Corporate Training</Link>

          <div className="flex flex-col gap-3">
            <button className={`flex items-center justify-between gap-2 ${mobileLink} font-semibold w-full text-left`} onClick={() => handleMobileSection(1)}>
              Our Services <FaChevronDown />
            </button>
            {mobileOne && (
              <div className="flex flex-col gap-2 pl-2">
                {services.map((s) => (
                  <button
                    key={s.label}
                    onClick={() => { setMobileOpen(false); openModal("get-in-touch"); }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/10 text-left transition-colors`}
                  >
                    <IconBox icon={s.icon} />
                    <span className={`${mobileSubLink} text-sm font-medium`}>{s.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <button className={`flex items-center justify-between gap-2 ${mobileLink} font-semibold w-full text-left`} onClick={() => handleMobileSection(2)}>
              Our Products <FaChevronDown />
            </button>
            {mobileTwo && (
              <div className="flex flex-col gap-3 pl-4 border-l border-white/20">
                {products.map((p) => (
                  <Link key={p.name} to={p.to} className={mobileSubLink} onClick={() => setMobileOpen(false)}>{p.name}</Link>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <button className={`flex items-center justify-between gap-2 ${mobileLink} font-semibold w-full text-left`} onClick={() => handleMobileSection(3)}>
              Academy <FaChevronDown />
            </button>
            {mobileThree && (
              <div className="flex flex-col gap-3 pl-4 border-l border-white/20">
                {academyLinks.map((a) => (
                  <Link key={a.to} to={a.to} className={mobileSubLink} onClick={() => setMobileOpen(false)}>{a.label}</Link>
                ))}
             
              
              </div>
            )}
          </div>

          <button onClick={() => { setMobileOpen(false); openModal("become-partner"); }} className={`px-4 py-3 font-semibold rounded-full text-center border transition-all ${isLight ? "border-blue-100 text-blue-100" : "border-white text-white"}`}>
            Become a Partner
          </button>
          <button onClick={() => { setMobileOpen(false); openModal("get-in-touch"); }} className={`px-4 py-3 font-semibold rounded-full text-center mb-24 transition-all ${isLight ? "bg-blue-100 text-white" : "bg-white text-blue-100"}`}>
            Contact Us
          </button>
        </div>
      )}
        </>,
        document.body
      )}
    </div>
  );
};

export default Navbar;
